import React, { useEffect, useState } from 'react';
import { Sparkles, Loader2, Save, Mail, Heart } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { adminAPI } from '../services/api';

/** Admin: AI moderation + Monday reviews recap toggles, with "send recap now". */
const ReviewAiSettings = () => {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [thanking, setThanking] = useState(false);
  useEffect(() => { adminAPI.reviewAiSettings().then(setS).catch(() => setS(null)); }, []);
  if (!s) return null;
  const save = async () => {
    setSaving(true);
    try {
      const saved = await adminAPI.saveReviewAiSettings({ moderation_enabled: !!s.moderation_enabled, weekly_recap_enabled: !!s.weekly_recap_enabled, auto_thanks_enabled: !!s.auto_thanks_enabled });
      setS((prev) => ({ ...prev, ...saved }));
      toast.success('Réglages IA enregistrés');
    } catch (e) { toast.error(e.response?.data?.detail || 'Enregistrement impossible'); } finally { setSaving(false); }
  };
  const sendRecap = async () => {
    setSending(true);
    try {
      const r = await adminAPI.sendReviewsRecap();
      toast.success(r.status === 'sent' || r.status === 'simulated' ? `Récap envoyé à ${r.to}` : `Récap : ${r.status}`);
    } catch (e) { toast.error('Envoi impossible'); } finally { setSending(false); }
  };
  const runThanks = async () => {
    setThanking(true);
    try { const r = await adminAPI.runAutoThanks(); toast.success(`${r.replied ?? 0} remerciement(s) publié(s) · ${r.candidates ?? 0} avis 5★ éligibles`); } catch (e) { toast.error('Exécution impossible'); } finally { setThanking(false); }
  };
  return (
    <div className="bg-white rounded-lg shadow-sm p-4 mb-4 flex flex-wrap items-center gap-4" data-testid="review-ai-settings">
      <div className="flex items-center gap-2 text-sm font-semibold text-gray-900"><Sparkles size={18} className="text-violet-500" /> IA avis</div>
      <label className="inline-flex items-center gap-2 text-sm text-gray-700" title="Chaque nouvel avis est analysé (insultes, spam, hors sujet). Si suspect — ou si l'IA est indisponible — il est masqué en attente de votre validation.">
        <input type="checkbox" checked={!!s.moderation_enabled} onChange={(e) => setS({ ...s, moderation_enabled: e.target.checked })} data-testid="review-ai-moderation" className="rounded border-gray-300 text-orange-500" /> Modération automatique
      </label>
      <label className="inline-flex items-center gap-2 text-sm text-gray-700" title="Chaque lundi 9h : avis reçus, note moyenne, avis sans réponse et en attente.">
        <input type="checkbox" checked={!!s.weekly_recap_enabled} onChange={(e) => setS({ ...s, weekly_recap_enabled: e.target.checked })} data-testid="review-ai-weekly" className="rounded border-gray-300 text-orange-500" /> Récap hebdo (lundi)
      </label>
      <label className="inline-flex items-center gap-2 text-sm text-gray-700" title="Chaque matin : remerciement rédigé par l'IA publié sous les avis 5 étoiles restés sans réponse 48 h (marqué « auto », modifiable).">
        <input type="checkbox" checked={!!s.auto_thanks_enabled} onChange={(e) => setS({ ...s, auto_thanks_enabled: e.target.checked })} data-testid="review-ai-auto-thanks" className="rounded border-gray-300 text-orange-500" /> Remerciement auto 5★ (48 h)
      </label>
      {!s.ai_available && <span className="text-xs text-red-600">Clé IA absente</span>}
      {s.pending > 0 && <span className="text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5" data-testid="review-ai-pending">{s.pending} avis en attente</span>}
      <div className="ml-auto flex items-center gap-2">
        <Button size="sm" variant="outline" onClick={runThanks} disabled={thanking} data-testid="review-ai-run-thanks" className="h-8">{thanking ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Heart size={14} className="mr-1" />} Remercier maintenant</Button>
        <Button size="sm" variant="outline" onClick={sendRecap} disabled={sending} data-testid="review-ai-send-recap" className="h-8">{sending ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Mail size={14} className="mr-1" />} Envoyer le récap maintenant</Button>
        <Button size="sm" onClick={save} disabled={saving} data-testid="review-ai-save" className="bg-orange-500 hover:bg-orange-600 text-white h-8">{saving ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Save size={14} className="mr-1" />} Enregistrer</Button>
      </div>
    </div>
  );
};

export default ReviewAiSettings;
